import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageSquare, Sparkles, Activity, User as UserIcon, ChevronRight, MapPin, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/app/AppShell";
import MatchingGate from "@/components/app/MatchingGate";
import { IconAvatar } from "@/components/app/IconAvatar";
import { claimInactiveMatchCredit, getBillingCatalog, getMatches, markMatchSeen, startPartnership } from "@/lib/api";
import { getProfile } from "@/lib/storage";
import { PartnershipRulesModal } from "@/components/app/LifecyclePanels";
import InvitePartner from "@/components/app/InvitePartner";

export default function Matches() {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [rulesFor, setRulesFor] = useState(null); // { match, profile }
  const [loading, setLoading] = useState(true);
  const [productFeatures, setProductFeatures] = useState({ partnershipStakes: false, workspaceTrialDays: 7 });
  const soloMode = getProfile()?.usageMode === "solo";

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (soloMode) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const [response, catalog] = await Promise.all([
          getMatches(),
          getBillingCatalog().catch(() => null),
        ]);
        if (!cancelled) {
          setMatches(response.matches || []);
          if (catalog?.features) setProductFeatures(catalog.features);
        }
      } catch (error) {
        toast("Could not load matches.", { description: error.message });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [soloMode]);

  const openChat = async (match) => {
    try {
      await markMatchSeen(match.id);
      setMatches((items) => items.map((item) => item.id === match.id ? { ...item, isNew: false } : item));
    } catch (error) {
      toast("Could not update this match.", { description: error.message });
    }
    navigate(`/chat/${match.id}`);
  };

  const requestStart = (match, profile) => {
    setRulesFor({ match, profile });
  };

  const confirmStart = async () => {
    if (!rulesFor) return;
    try {
      const response = await startPartnership(rulesFor.match.id);
      setRulesFor(null);
      if (response.partnership.pending) {
        toast("Partnership request sent.", { description: `Your seven-day Starter Sprint begins when ${rulesFor.profile.fullName.split(" ")[0]} confirms.` });
        navigate(`/chat/${rulesFor.match.id}`);
      } else {
        toast("Partnership confirmed.", { description: "Your seven-day Starter Sprint and shared workspace are ready." });
        navigate(`/workspace/${response.partnership.id}`);
      }
    } catch (error) {
      toast("Could not start the partnership.", { description: error.message });
    }
  };

  const claimInactive = async (match) => {
    if (!window.confirm("Close this inactive match and restore your match capacity? This cannot be undone.")) return;
    try {
      await claimInactiveMatchCredit(match.id);
      setMatches((items) => items.filter((item) => item.id !== match.id));
      toast("Match closed and capacity restored.", { description: "Only your match entitlement was restored; the inactive account did not receive one." });
    } catch (error) {
      toast("Could not complete the inactivity claim.", { description: error.message });
    }
  };
  return (
    <AppShell>
      <MatchingGate>
      <div className="px-6 sm:px-10 py-10 max-w-6xl mx-auto">
        <div className="mb-10">
          <div className="overline mb-3">Matches</div>
          <h1 className="font-serif-editorial font-light tracking-tighter text-4xl sm:text-5xl leading-[1.05]">
            The people who <em className="italic text-[#F59E0B]">said yes.</em>
          </h1>
          <p className="mt-3 text-[#A1A1AA] font-light max-w-xl">
            Start a conversation to feel the fit. When you're both aligned, promote it to an active partnership and open your workspace.
          </p>
        </div>

        {loading ? (
          <div className="text-sm text-[#A1A1AA] py-10 text-center">Loading matches...</div>
        ) : matches.length === 0 ? (
          <div className="hm-card p-10 rounded-md text-center max-w-lg mx-auto" data-testid="matches-empty">
            <Sparkles className="w-8 h-8 text-[#F59E0B] mx-auto mb-4" strokeWidth={1.25} />
            <h2 className="font-serif-editorial text-2xl mb-3">No matches yet.</h2>
            <p className="text-[#A1A1AA] font-light mb-6">Head to Discover, join a cohort to be paired by the team, or invite someone you already know below.</p>
            <div className="flex flex-wrap justify-center gap-2">
              <Link to="/discover" className="hm-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold">
                Go to Discover <ChevronRight className="w-4 h-4" />
              </Link>
              <Link to="/cohorts" className="hm-btn-ghost inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm">See cohorts</Link>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5" data-testid="matches-grid">
            {matches.map((m) => {
              const p = m.partner.profile;
              const isActive = m.isActive;
              return (
                <div key={m.id} className="hm-card p-6 rounded-md" data-testid={`match-card-${m.partner.userId}`}>
                  <div className="flex items-start gap-4 mb-4">
                    <IconAvatar iconKey={p.profileIcon} size="lg" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-serif-editorial text-xl leading-tight truncate">{p.fullName}</h3>
                        {m.isNew && !isActive && (
                          <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm bg-[#F59E0B] text-black">New</span>
                        )}
                        {isActive && (
                          <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm border border-[#F59E0B]/40 text-[#F59E0B]">Active</span>
                        )}
                        {m.source && m.source !== "discover" && (
                          <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm border border-white/15 text-[#A1A1AA]">{m.source === "invite" ? "Via invite" : "Cohort"}</span>
                        )}
                      </div>
                      <div className="text-xs text-[#A1A1AA] flex items-center gap-3 mt-1">
                        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{p.city}</span>
                        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{p.timezone}</span>
                      </div>
                      <p className="mt-3 text-sm text-[#F5F5F0] font-light line-clamp-2">{p.goalDescription}</p>
                      <div className="mt-2 inline-flex items-center gap-1.5 rounded-sm border border-sky-300/30 bg-sky-300/[.07] px-2 py-1 text-[10px] uppercase tracking-[.14em] text-sky-100" title="Shared free time over the next seven days; 100 means at least 14 hours">
                        <Clock className="h-3 w-3" /> {m.overlapScore == null ? "Overlap unavailable" : `${m.overlapScore} overlap · ${Math.round(m.sharedMinutesWeek / 60 * 10) / 10}h/wk`}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {p.goalTags.slice(0, 3).map((t) => (
                          <span key={t} className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border border-[#F59E0B]/30 bg-[#F59E0B]/5 text-[#F59E0B]">{t}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  {m.inactivity?.visible && <InactivityNotice inactivity={m.inactivity} partnerName={p.fullName.split(" ")[0]} onClaim={() => claimInactive(m)} />}
                  <div className="flex items-center gap-2 pt-3 border-t border-white/5">
                    <Link to={`/profile/${m.partner.userId}`} data-testid={`match-view-${m.partner.userId}`} className="hm-btn-ghost inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs">
                      <UserIcon className="w-3.5 h-3.5" /> Profile
                    </Link>
                    <button onClick={() => openChat(m)} data-testid={`match-chat-${m.partner.userId}`} className="hm-btn-ghost inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs">
                      <MessageSquare className="w-3.5 h-3.5" /> Chat
                    </button>
                    {m.partnershipId && !m.partnershipEnded && <Link to={`/workspace/${m.partnershipId}?tab=calendar`} className="hm-btn-ghost inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs"><Clock className="h-3.5 w-3.5" /> Shared times</Link>}
                    <div className="ml-auto">
                      {isActive ? (
                        <Link to={`/workspace/${m.partnershipId}`} className="hm-btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold">
                          <Activity className="w-3.5 h-3.5" /> Open workspace
                        </Link>
                      ) : m.partnershipEnded ? (
                        <span className="text-xs text-[#7a7a80]">Partnership ended</span>
                      ) : m.partnershipPending && m.partnershipStartedByMe ? (
                        <span className="text-xs text-[#A1A1AA]">Waiting for confirmation</span>
                      ) : (
                        <button onClick={() => requestStart(m, p)} data-testid={`match-start-${m.partner.userId}`} className="hm-btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold">
                          {m.needsYourConfirmation ? "Join partnership" : "Start partnership"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {!loading && <div className="mt-10"><InvitePartner /></div>}
      </div>
      <PartnershipRulesModal
        open={!!rulesFor}
        partnerName={rulesFor?.profile?.fullName?.split(" ")[0]}
        onClose={() => setRulesFor(null)}
        onConfirm={confirmStart}
        stakesEnabled={Boolean(productFeatures.partnershipStakes)}
        trialDays={productFeatures.workspaceTrialDays || 7}
      />
      </MatchingGate>
    </AppShell>
  );
}

function InactivityNotice({ inactivity, partnerName, onClaim }) {
  return (
    <div className="mb-4 rounded-md border border-amber-500/25 bg-amber-500/[0.05] p-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
        <div className="flex-1">
          <p className="text-xs text-[#F5F5F0]">No response from {partnerName} yet.</p>
          <p className="mt-1 text-[11px] text-[#A1A1AA]">After seven days without any reply or partnership confirmation, you can close this match and restore your match capacity.</p>
          <button onClick={onClaim} disabled={!inactivity.eligible} className="hm-btn-ghost mt-3 px-3 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-50">
            {inactivity.eligible ? "Report inactivity & restore match" : `Available in ${inactivity.daysUntilEligible} day${inactivity.daysUntilEligible === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
